import { useCallback } from 'react';

import { useLocale, useT } from '../context/locale';

/**
 * Localised "3h ago" / "in 2m".
 *
 * `format.relative` returns English and cannot reach the dictionary — it is a
 * plain function. This hook does the same arithmetic and renders through the
 * translator, so the unit and the word order both follow the language.
 */
export function useRelative() {
  const t = useT();

  return useCallback(
    (value: string | null | undefined): string => {
      if (!value) return '—';

      const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
      const future = seconds < 0;
      const magnitude = Math.abs(seconds);
      if (magnitude < 5) return t('common.justNow');

      const amount =
        magnitude < 60
          ? t('time.seconds', { value: magnitude })
          : magnitude < 3600
            ? t('time.minutes', { value: Math.floor(magnitude / 60) })
            : magnitude < 86_400
              ? t('time.hours', { value: Math.floor(magnitude / 3600) })
              : t('time.days', { value: Math.floor(magnitude / 86_400) });

      return future ? t('common.in', { value: amount }) : t('common.ago', { value: amount });
    },
    [t],
  );
}

/**
 * Localised "5m" / "2h 10m" / "3d 4h".
 *
 * `format.duration` hardcodes the English unit letters; a Chinese console
 * showing "5m" beside 分钟 reads as an untranslated leftover.
 */
export function useDuration() {
  const t = useT();

  return useCallback(
    (totalSeconds: number): string => {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (hours >= 24) return t('time.dh', { days: Math.floor(hours / 24), hours: hours % 24 });
      if (hours) return t('time.hm', { hours, minutes });
      if (minutes) return t('time.minutes', { value: minutes });
      return t('time.seconds', { value: totalSeconds });
    },
    [t],
  );
}

/**
 * Localised absolute timestamps — "18 Sept 2026, 01:03" / "2026年9月18日 01:03".
 *
 * `format.dateTime` pins `en-GB` and so kept English month names in a Chinese
 * console. Same reason the relative formatter above had to become a hook: the
 * locale lives in React state, not in a module.
 */
export function useDateTime() {
  const { locale } = useLocale();

  return useCallback(
    (value: string | null | undefined): string => {
      if (!value) return '—';
      return new Date(value).toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        // Rounds are minutes apart and settle on an exact second — without this
        // two different rounds render as the same timestamp in the Draws table.
        second: '2-digit',
      });
    },
    [locale],
  );
}

/** Localised date without the clock — for day-granularity columns. */
export function useDateOnly() {
  const { locale } = useLocale();

  return useCallback(
    (value: string | null | undefined): string => {
      if (!value) return '—';
      return new Date(value).toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    [locale],
  );
}
